<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template>
	<v-data-table
		:items="filtered_items"
		:headers="headers"
		:options.sync="options"
		:server-items-length="items.length"
		:xfooter-props="footerProps"
		hide-default-footer
		class="k-asset-manager-table"
		xheight="calc(100% - 125px)"
		width="100%"
		fixed-header
		light
	>
		<template v-slot:item="{ item }">
			<tr class="k-table-row" :class="row_class(item)" @click="$emit('show_status', item)">
				<td v-for="(header, i) in headers" :class="[{'btn-column': i==0}, {'title-column': i==1}, {'center-text': header.align == 'center'}, {'subject-column':header.value=='subjects'}, {'nobr-column':header.value=='publish_status'}]">
					<v-icon v-if="header.value == 'button'" class="button" small>fas fa-arrow-up-right-from-square</v-icon>

					<div v-else-if="header.value == 'alignments'" :class="(item.asset.standards.length==0)?'k-asset-manager-table-no-alignments':''">
						{{item.asset.standards.length}}
					</div>
					<span v-else v-html="display_value(item, header.value)"></span>
				</td>
			</tr>
		</template>
	</v-data-table>
</template>

<script>
import { mapState } from 'vuex'

export default {
	name: 'AssetManagerTable',

	components: {},

	props: {
		tab: {type: Object, required: true},
		headers: {required: true},
		search: {required: true},
	},

	data() {
		return {
			init: false,
			items: [],
			options: {
				groupBy: [],
				groupDesc: [],
				itemsPerPage: -1,
				multiSort: false,
				mustSort: false,
				page: 1,
				sortBy: ['publish_status'],
				sortDesc: [false],
			},
			footerProps: {
				'items-per-page-options': [100, 50, 25, 10]
			},
		}
	},

	computed: {
		...mapState(['user_info', 'site_config']),

		filtered_items() {
			// filter
			let arr = this.items.filter(x=>{
				if (this.search.term) {
					let re = new RegExp(this.search.term, 'i')
					if (x.asset.title().search(re) == -1 && x.creator_name.search(re) == -1) {
						return false
					}
				}

				if (this.search.filters.status && this.search.filters.status != 'all') {
					if (x.asset.publish_status != this.search.filters.status) {
						return false
					}
				}

				// TODO FOR JON: implement grade and subject filters

				return true
			})

			// then sort
			let sort_by = this.options.sortBy[0] || 'publish_status'
			arr.sort((a,b) => {
				let val = 0
				if (sort_by == 'title') {
					val = U.natural_sort(a.asset.title(), b.asset.title())

				} else if (sort_by == 'submitted_date') {
					if ((a.asset.submitted_date??0+'') < (b.asset.submitted_date??0+'')) val = -1
					else if ((a.asset.submitted_date??0+'') > (b.asset.submitted_date??0+'')) val = 1

				} else if (sort_by == 'status_date') {
					if (a.asset.status_date < b.asset.status_date) val = -1
					else if (a.asset.status_date > b.asset.status_date) val = 1

				} else { // default: (sort_by == 'publish_status')
					let psa = window.get_publish_status_data(a.asset.publish_status).index
					let psb = window.get_publish_status_data(b.asset.publish_status).index
					if (psa != psb) val = psa - psb
					else {
						// if statuses are the same, go by status_date desc
						if (a.asset.status_date < b.asset.status_date) return 1
						if (a.asset.status_date > b.asset.status_date) return -1
						return 0
					}
				}

				// if we get to here, return val or -1*val
				if (this.options.sortDesc[0]) return -1 * val
				else return val
			})
			return arr
		},
	},

	watch: {
		options: {
			handler() {
				// TODO: we only want to load more assets if the page changes... currently we should never have too many items, so we're not paging for now
				// if (this.init) {
				// 	this.load_assets()
				// }
			}, 
			deep: true,
		}
	},

	emits: ['pay'],

	async mounted() {
		await this.load_assets()
	},

	methods: {
		async load_assets() {
			// make this an async function
			
			let load_asset_service = () => {
				return new Promise((resolve, reject) => {
					let { page, itemsPerPage } = this.options

					let payload = {
						user_id: this.user_info.user_id,
						asset_type: this.tab.key
					}

					U.ajax('get_unpublished_assets', payload, result => {
						resolve(result)
					})
				})
			}

			U.loading_start()
			let result = await load_asset_service()
			U.loading_stop()

			this.init = true
			if (!result.status == 'ok') {
				console.error('something went wrong: ', result)
			} else if (result.assets) {
				this.items = result.assets.map(x => {
					// store incoming data that doesn't get placed into the standard Resource or Lesson class
					let o = {
						creator_name: x.creator_name,
						creator_email: x.creator_email,
						roles: x.roles,
						subjects: x.subjects,
						grades: x.grades,
					}
					// instantiate the asset class and store some other data
					if (x.lesson_title) {
						o.asset_class_type = 'lesson'
						o.asset = new Lesson(x)
					} else {
						o.asset_class_type = 'resource'
						o.asset = new Resource(x)
					}
					return o
				})
			}
		},

		row_class(item) {
			let o = window.get_publish_status_data(item.asset.publish_status)
			return `${o.color} lighten-5`
		},

		display_value(item, key) {
			let value = item[key]
			if (key.includes('date') || key.includes('_at')) {
				value = item.asset[key]
				return value
				if (empty(value)) return ''
				let date = new Date(value)
				if (date != 'Invalid Date'){
					return date.toLocaleDateString()	// this renders the date in the local timezone
				} else {
					return value
				}

			} else if (key == 'publish_status') {
				let o = window.get_publish_status_data(item.asset.publish_status)
				
				return `<span class="${o.color}--text text--darken-4"><i class="${o.icon}"></i> ${o.text}</span>`

			} else if (key == 'subjects') {
				return value.map(x => x.text).join(', ')

			} else if (key == 'grades') {
				return value.join(', ')

			} else if (key == 'title') {
				return `<i class="mr-1 fas ${item.asset.icon()}"></i>${item.asset.title()}`
			}

			return value
		},

	},
}
</script>

<style lang="scss">
.k-asset-manager-table {
	height: 100%;
	width: 100%;

	td, th {
		font-size:12px!important;
		line-height:15px;
	}
	th {
		white-space: nowrap;
		height:32px!important;
	}

	.k-table-row {
		cursor: pointer;
		.button {
			cursor: pointer;
		}

		.btn-column {
			width:5px;
			padding-left:8px!important;
			padding-right:0!important;
		}
		.title-column {
			width: 340px;
		}
		.subject-column {
			max-width:160px;
		}
		.nobr-column {
			white-space:nowrap;
		}

		.center-text {
			text-align: center;
		}
	}

	.k-asset-manager-table-no-alignments {
		font-weight:bold;
		color:$v-red-darken-3;
	}
}
</style>